
export const formatCurrency = (amount: number, currencyCode: string = 'USD'): string => {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
        }).format(amount);
    } catch (error) {
        console.warn("Invalid currency code:", currencyCode);
        return `$${amount}`; // Fallback
    }
};

export const formatDate = (dateString: any, formatPattern: string = 'DD/MM/YYYY'): string => {
    if (!dateString) return '-';

    let date: Date;
    // Handle Firestore Timestamp (duck typing)
    if (typeof dateString === 'object' && dateString?.seconds) {
        date = new Date(dateString.seconds * 1000);
    } else {
        date = new Date(dateString);
    }

    if (isNaN(date.getTime())) return '-'; // Return safe string instead of object

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    if (formatPattern === 'MM/DD/YYYY') {
        return `${month}/${day}/${year}`;
    } else if (formatPattern === 'YYYY-MM-DD') {
        return `${year}-${month}-${day}`;
    } else {
        // Default to DD/MM/YYYY
        return `${day}/${month}/${year}`;
    }
};

export const formatDateTime = (dateString: any, formatPattern: string = 'DD/MM/YYYY', timeZone: string = 'UTC'): string => {
    if (!dateString) return '-';

    let date: Date;
    // Handle Firestore Timestamp
    if (typeof dateString === 'object' && dateString?.seconds) {
        date = new Date(dateString.seconds * 1000);
    } else {
        date = new Date(dateString);
    }

    if (isNaN(date.getTime())) return '-';

    try {
        // Get parts in the target timezone
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: timeZone || 'UTC'
        };

        // formats to "day/month/year, hh:mm" or similar depending on locale, 
        // but we want strict control over pattern. 
        // We can use formatToParts.
        const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);

        const getPart = (name: string) => parts.find(p => p.type === name)?.value || '';

        const day = getPart('day');
        const month = getPart('month');
        const year = getPart('year');
        const hour = getPart('hour');
        const minute = getPart('minute');

        let datePart = '';
        if (formatPattern === 'MM/DD/YYYY') {
            datePart = `${month}/${day}/${year}`;
        } else if (formatPattern === 'YYYY-MM-DD') {
            datePart = `${year}-${month}-${day}`;
        } else {
            datePart = `${day}/${month}/${year}`;
        }

        return `${datePart} ${hour}:${minute}`;
    } catch (e) {
        console.warn("Date formatting error (timezone likely invalid):", e);
        return formatDate(dateString, formatPattern) + ' ' + date.toLocaleTimeString();
    }
};

export const parseDate = (dateString: string, formatPattern: string): string | null => {
    if (!dateString) return null;

    // Attempt to parse based on format
    // Simple implementation for standard formats
    const parts = dateString.split(/[-/.]/);
    if (parts.length !== 3) return null;

    let day, month, year;

    if (formatPattern === 'DD/MM/YYYY') {
        day = parts[0];
        month = parts[1];
        year = parts[2];
    } else if (formatPattern === 'MM/DD/YYYY') {
        month = parts[0];
        day = parts[1];
        year = parts[2];
    } else if (formatPattern === 'YYYY-MM-DD') {
        year = parts[0];
        month = parts[1];
        day = parts[2];
    } else {
        // Default fallthrough to DD/MM/YYYY
        day = parts[0];
        month = parts[1];
        year = parts[2];
    }

    // Pad
    day = day.padStart(2, '0');
    month = month.padStart(2, '0');

    // Create ISO string YYYY-MM-DD
    const isoDate = `${year}-${month}-${day}`;

    // Validate
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return null;

    return isoDate;
};
